//@author: travis

import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    input,
    Input,
    OnInit,
    output,
    Output,
    signal,
    ViewChild,
    ViewEncapsulation,
    ChangeDetectionStrategy,
} from "@angular/core";
import { CropperPosition, ImageCroppedEvent, ImageCropperComponent, ImageTransform } from "ngx-image-cropper";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { jSith } from "../../util/jquery-replacement";

export interface ImageUploadModelI {
    getImageData?: () => Promise<string>;
    getImageFileName?: () => string | undefined;
    getImageType?: () => string | undefined;
    getWidth?: () => number | undefined;
    getErrorMsg?: () => string;
    resetImageState?: () => void;
}

export interface ImageActionDataI {
    imageName?: string;
    imageDesc?: string;
    imageFileName?: string;
    imageType?: string;
    imageData?: string; //base64 image or imageUri,
    imageId?: number;
    imageTypeId?: number;
}

@TypeManagerDecorator("s25-ng-image-upload")
@Component({
    selector: "s25-ng-image-upload",
    encapsulation: ViewEncapsulation.Emulated,
    styles: [
        `
            :host ::ng-deep image-cropper .overlay {
                --cropper-overlay-color: var(--color--border) !important;
            }
        `,
    ],
    template: `
        @if (!hasCropper) {
            <div class="upload-btn-wrapper">
                <input
                    type="file"
                    #file
                    (change)="fileChangeEvent(file.files)"
                    class="ngUnboundedUpload c-margin-bottom--half"
                    value="'Choose a file'"
                    aria-label="Upload image"
                />
                <button class="file-btn">Upload</button>
            </div>
            @if (showPreview() && imageUri) {
                <div class="s25-image-body c-margin-top--half">
                    <img [src]="imageUri" alt="Image Upload Preview" [style.max-width]="previewWidth()" />
                </div>
            }
            @if (showClear() && imageUri) {
                <button class="aw-button aw-button--outline c-margin-top--half" type="button" (click)="clear.emit()">
                    Clear Image
                </button>
            }
        }

        @if (hasCropper) {
            @if (options.hasRotate || options.hasZoom) {
                <div class="c-margin-bottom--single">
                    @if (options.hasRotate) {
                        <button class="aw-button aw-button--outline" (click)="rotate(-1)">Rotate Left</button>
                        <button class="aw-button aw-button--outline" (click)="rotate(1)">Rotate Right</button>
                    }
                    @if (options.hasZoom) {
                        <button class="aw-button aw-button--outline" (click)="scale(-1)">Zoom Out</button>
                        <button class="aw-button aw-button--outline" (click)="scale(1)">Zoom In</button>
                    }
                </div>
            }
            <div>
                <image-cropper
                    #cropper
                    [imageBase64]="this.imageBase64String"
                    [maintainAspectRatio]="false"
                    [cropperMinHeight]="minHeight"
                    [cropperMaxHeight]="maxHeight"
                    [cropperMinWidth]="minWidth"
                    [cropperMaxWidth]="maxWidth"
                    [canvasRotation]="rotation"
                    [cropper]="cropperPosition()"
                    (imageCropped)="imageCropped($event)"
                    (imageLoaded)="imageLoaded($event)"
                    (cropperReady)="cropperReady($event)"
                    (loadImageFailed)="loadImageFailed()"
                    [transform]="transform"
                    [output]="'base64'"
                ></image-cropper>
            </div>
            <div class="upload-btn-wrapper c-margin-top--single">
                <input
                    type="file"
                    #file
                    (change)="fileChangeEvent(file.files)"
                    aria-label="Upload image"
                    class="ngCroppieUpload"
                    value="'Choose a file'"
                />
                <button class="file-btn">Upload</button>
            </div>
        }

        @if (showNote()) {
            <div class="ngFinePrint c-margin-bottom--half">
                Note: a light gray background is used behind your image to display any transparent text or designs. It
                is not part of the image that will be uploaded.
            </div>
        }
    `,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class S25ImageUploadComponent implements OnInit {
    @Input() model: ImageUploadModelI = {}; //allows access to some image details
    @Input()
    set imageUri(value: string | undefined) {
        this._imageUri = value;
        this.originalInputImageUri = value;
        this.onImageUriChange();
    }
    get imageUri(): string | undefined {
        return this._imageUri;
    }
    @Input() hasCropper: boolean = false;
    @Input() minWidth?: number;
    @Input() maxWidth?: number;
    @Input() minHeight?: number;
    @Input() maxHeight?: number;
    @Input() isModal?: boolean;

    @Input() options: { hasRotate?: boolean; hasZoom?: boolean } = {};

    readonly showPreview = input<boolean>(true);
    /** Max-width for the preview image; number treated as px, string used as CSS value */
    readonly previewWidth = input(undefined, {
        transform: (pw: number | string) => (typeof pw === "number" ? pw + "px" : pw),
    });
    readonly showClear = input<boolean>(false);
    readonly showNote = input<boolean>(true);
    readonly clear = output<void>();

    @Output() modelChange = new EventEmitter();
    @Output() uploadedImageChange = new EventEmitter<string>();
    @ViewChild(ImageCropperComponent) cropperChild?: ImageCropperComponent;

    private _imageUri?: string;
    private originalInputImageUri?: string;
    uploadedFile?: File;
    imageBase64String?: string; //had trouble sending image to ngx-image-cropper in any other format
    init = true;
    croppedImage: string = "";
    croppedImageWidth?: number;
    SUPPORTED_TYPES: string[] = ["png", "jpg", "jpeg", "gif"];
    errorMsg = "";
    transform: ImageTransform = { scale: 1 };
    rotation: number = 0;
    cropperPosition = signal<CropperPosition | undefined>(undefined);

    constructor(
        private elementRef: ElementRef,
        private cd: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        //expose ability for creator to get image data
        this.createModel();
    }

    createModel() {
        this.model = this.model || {};
        this.model.getImageData = () => {
            if (this.imageUri) {
                if (this.hasCropper) {
                    if (!this.croppedImage) {
                        return jSith.when(this.imageUri);
                    }
                    return jSith.when(this.croppedImage);
                } else {
                    return jSith.when(this.imageUri);
                }
            } else {
                return jSith.when(); //empty promise bc no image has been uploaded and no imageUri was passed in initially
            }
        };

        this.model.getImageFileName = () => {
            if (this.uploadedFile) {
                return this.uploadedFile.name;
            }
        };

        this.model.getImageType = () => {
            const fileName = this.model.getImageFileName?.();
            return fileName?.substring(fileName.lastIndexOf(".") + 1);
        };

        //expose ability for creator to get width
        this.model.getWidth = () => {
            if (this.hasCropper) {
                return this.croppedImageWidth;
            }
        };

        this.model.getErrorMsg = () => {
            return this.errorMsg;
        };

        this.model.resetImageState = () => {
            this.uploadedFile = undefined;
            this.errorMsg = "";
            this.croppedImage = "";
            this.croppedImageWidth = undefined;
            this._imageUri = this.originalInputImageUri;
            this.imageBase64String = this.originalInputImageUri;
            this.rotation = 0;
            this.transform = { scale: 1 };
            this.cropperPosition.set(undefined);
            this.cd.detectChanges();
        };

        this.modelChange.emit(this.model);
    }

    rotate(direction: number) {
        this.rotation += direction;
        this.cd.detectChanges();
    }

    scale(scaleStep: number) {
        const currentScale = this.transform.scale ?? 1;
        if (currentScale + scaleStep < 1) {
            return;
        }
        const nextScale = currentScale + scaleStep;
        this.transform = { ...this.transform, scale: nextScale };
        this.cd.detectChanges();
    }

    getBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = (error) => reject(error);
            reader.readAsDataURL(file);
        });
    }

    setImageBase64(base64ImgStr: string) {
        //'png'| 'jpg'| 'jpeg'| 'gif'){
        this.imageBase64String = base64ImgStr;
        this.uploadedImageChange.emit(this.imageBase64String);
        this.cd.detectChanges();
    }

    async fileChangeEvent(files: FileList | null | undefined) {
        this.errorMsg = "";
        if (files && files[0]) {
            const file = files[0];

            const sizeMb = file.size / 1024 / 1024;
            let fileType = "UNSUPPORTED";
            const fileTypeLoc = ("" + file.name).lastIndexOf(".");
            if (fileTypeLoc > -1) {
                fileType = ("" + file.name).substring(fileTypeLoc + 1).toLowerCase();
            }

            if (this.SUPPORTED_TYPES.indexOf(fileType) < 0) {
                this.errorMsg =
                    "The file type '" + fileType + "' is not supported. Please upload a png, jpg, jpeg, or gif file.";
            } else if (sizeMb > 5.0) {
                this.errorMsg = "Please upload a file with a size of less than 5MB.";
            } else {
                this.uploadedFile = file;
                this.setUploadedImageUri(await this.getBase64(file));
            }
        } else {
            this.errorMsg =
                "Your browser does not support the FileReader API. Please use a modern browser for this functionality.";
        }
        if (this.errorMsg) {
            this.isModal ? this.uploadedImageChange.emit(this.imageBase64String ?? "") : alert(this.errorMsg);
        }
        this.cd.detectChanges();
    }

    imageCropped(event: ImageCroppedEvent) {
        if (event) {
            this.croppedImage = event.base64 ?? "";
            this.croppedImageWidth = event.width;
            this.uploadedImageChange.emit(this.croppedImage);
            this.cd.detectChanges();
        }
    }

    imageLoaded(event: unknown) {
        void event;
        this.cd.detectChanges();
        // show cropper
    }

    cropperReady(event: { width: number; height: number }) {
        // cropper ready: set initial crop
        this.cropperPosition.set({
            x1: 0,
            y1: 0,
            x2: Math.min(event.width, this.maxWidth || event.width),
            y2: Math.min(event.height, this.maxHeight || event.height),
        });
        const cropped = this.cropperChild?.crop();
        if (cropped) {
            this.imageCropped(cropped);
        }
        this.cd.detectChanges();
    }

    loadImageFailed() {
        this.cd.detectChanges();
        // show message
    }

    onImageUriChange = () => {
        // Only treat as base64 when user uploaded actual image data; URLs are for preview only
        const v = this.imageUri;
        if (v && !v.startsWith("http://") && !v.startsWith("https://")) {
            this.setImageBase64(v);
        }
    };

    private setUploadedImageUri(value: string) {
        this._imageUri = value;
        this.onImageUriChange();
    }
}
